'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { Hourglass, MatchingButton, MatchingCard, NoMatchCard } from './styles';

import { useNavigateTo } from '@dh/hooks';
import { BodyMedium, TitleLarge, TitleMedium } from '@dh/styles';
import { Institution, Match } from '@dh/types';
import { MatchCard } from '@dh/components';
import { fetchMatches } from '@dh/api/matching';

import { ROUTES } from '../../../../constants';
import { isCompany } from '@dh/utils';
import { useRouter } from 'next/navigation';

type Props = {
    institution: Institution;
};

const DashboardScreenMatchingSection = ({ institution }: Props) => {
    const t = useTranslations('matching');

    const router = useRouter();
    const navigateTo = useNavigateTo();

    const [matches, setMatches] = useState<Match[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const fetchData = useCallback(async () => {
        setIsLoading(true);

        const { matches, error: errorMatches } = await fetchMatches();
        !errorMatches && setMatches(matches);

        setIsLoading(false);
    }, []);
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const renderEmptyMatches = () => {
        if (isCompany(institution)) {
            const isMatchingEnabledForCompany =
                !!institution.info && !!institution.email && institution.isPublic;

            if (isMatchingEnabledForCompany) {
                return (
                    <>
                        <TitleLarge>{t('matches_found.my_matches')}</TitleLarge>
                        <NoMatchCard>
                            <Hourglass />

                            <TitleMedium mt={2} mb={1}>
                                {t('matches_found.company.no_match.title')}
                            </TitleMedium>
                            <BodyMedium>
                                {t('matches_found.company.no_match.description')}
                            </BodyMedium>
                        </NoMatchCard>
                    </>
                );
            } else
                return (
                    <>
                        <TitleLarge>{t('matches_found.company.no_profile.title')}</TitleLarge>
                        <MatchingButton
                            label={t('matches_found.company.no_profile.gotoprofile')}
                            onClick={() => navigateTo(ROUTES.Profile, '?tab=Institution')}
                        />
                    </>
                );
        } else
            return (
                <>
                    <TitleLarge>{t('matches_found.organization.no_match.title')} </TitleLarge>
                    <MatchingButton
                        label={t('matches_found.organization.no_match.gotomatching')}
                        onClick={() => navigateTo(ROUTES.Matching)}
                    />
                </>
            );
    };

    const renderMatchOverview = () => {
        return (
            <>
                <TitleLarge>
                    {isCompany(institution)
                        ? t('matches_found.my_matches')
                        : t('matches_found.my_themes')}
                </TitleLarge>
                {matches.map((match) => (
                    <MatchCard
                        key={match.documentId}
                        viewer={institution.type}
                        match={match}
                        onClick={() =>
                            router.push(
                                `/matching/${match.documentId}?details=${match.matchedInstitutions.length > 0 ? 'matches' : 'info'}`
                            )
                        }
                    />
                ))}
            </>
        );
    };
    return (
        <MatchingCard>
            {matches.length === 0 || isLoading ? renderEmptyMatches() : renderMatchOverview()}
        </MatchingCard>
    );
};
export { DashboardScreenMatchingSection };
